import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang } from "@/i18n/LangContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/jsnv4v6a_1000368650.jpg";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLinks = [
    { to: "/", label: t("home") },
    { to: "/construction", label: t("construction") },
    { to: "/travaux", label: t("travaux") },
  ];

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}
        initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src={LOGO_URL} alt="KMSN" className="h-10 w-10 object-contain rounded" />
            <span className="font-heading text-white text-lg tracking-wide hidden sm:block">KMSN</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={`text-sm tracking-[0.15em] uppercase font-body transition-colors duration-300 ${location.pathname === link.to ? "text-[#C8A951]" : "text-white/70 hover:text-white"}`} data-testid={`nav-${link.label.toLowerCase()}-link`}>
                {link.label}
              </Link>
            ))}
            <LanguageSwitcher />
            <a href="#contact" className="border border-[#C8A951] text-[#C8A951] px-6 py-2 text-sm tracking-wider uppercase hover:bg-[#C8A951] hover:text-black transition-all duration-300 font-body" data-testid="nav-contact-btn">{t("contact")}</a>
          </div>
          <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-toggle">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} data-testid="nav-mobile-menu">
            {navLinks.map((link, i) => (
              <motion.div key={link.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ delay: i * 0.1 }}>
                <Link to={link.to} className={`text-2xl font-heading tracking-wide ${location.pathname === link.to ? "text-[#C8A951]" : "text-white"}`}>{link.label}</Link>
              </motion.div>
            ))}
            <LanguageSwitcher />
            <motion.a href="#contact" className="border border-[#C8A951] text-[#C8A951] px-8 py-3 text-lg tracking-wider uppercase mt-4 hover:bg-[#C8A951] hover:text-black transition-all duration-300" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} onClick={() => setMenuOpen(false)}>
              {t("contact")}
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
